import { Link } from "react-router-dom";
import { Languages, ArrowLeft, Download, Package, Film } from "lucide-react";
import { Button } from "../components/ui/button";

const ASSETS = [
  {
    key: "profile",
    title: "Profile / Avatar",
    size: "1024 × 1024",
    use: "Instagram, LinkedIn, X profile picture",
    src: "/social/profile.png",
  },
  {
    key: "linkedin-banner",
    title: "LinkedIn Banner",
    size: "1584 × 396",
    use: "LinkedIn company page cover",
    src: "/social/linkedin_banner.png",
  },
  {
    key: "youtube-banner",
    title: "YouTube Banner",
    size: "2560 × 1440",
    use: "YouTube channel art (safe area centered)",
    src: "/social/youtube_banner.png",
  },
  {
    key: "post-product",
    title: "Feed Post — Product",
    size: "1080 × 1080",
    use: "Instagram / LinkedIn / X feed",
    src: "/social/post_product.png",
  },
  {
    key: "post-disciplines",
    title: "Feed Post — Disciplines",
    size: "1080 × 1080",
    use: "\"Built for OTs, STs, PTs\" awareness post",
    src: "/social/post_disciplines.png",
  },
  {
    key: "post-stat",
    title: "Feed Post — Stat",
    size: "1080 × 1080",
    use: "Headline statistic / hook post",
    src: "/social/post_stat.png",
  },
  {
    key: "post-quote",
    title: "Feed Post — Quote",
    size: "1080 × 1080",
    use: "Clinician quote / testimonial",
    src: "/social/post_quote.png",
  },
  {
    key: "post-testimonial",
    title: "Testimonial Template",
    size: "1080 × 1080",
    use: "Swap in your own patient / clinician quote",
    src: "/social/post_testimonial_template.png",
  },
  {
    key: "story-hero",
    title: "Story — Hero",
    size: "1080 × 1920",
    use: "Instagram / Facebook Story hero",
    src: "/social/story_hero.png",
  },
  {
    key: "story-cta",
    title: "Story — CTA",
    size: "1080 × 1920",
    use: "Story with call-to-action / swipe-up",
    src: "/social/story_cta.png",
  },
];

function filenameFromSrc(src) {
  return src.split("/").pop();
}

const VIDEOS = [
  {
    key: "ad-vertical",
    title: "Vertical ad · 9:16",
    size: "1080 × 1920 · 52s",
    use: "TikTok · Instagram Reels · YouTube Shorts",
    src: "/clinico_ad_vertical.mp4",
  },
  {
    key: "ad-square",
    title: "Square ad · 1:1",
    size: "1080 × 1080 · 52s",
    use: "Instagram feed · LinkedIn · Facebook",
    src: "/clinico_ad_square.mp4",
  },
];

export default function Brand() {
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900" data-testid="brand-page">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
            <div className="w-9 h-9 rounded-lg bg-zinc-900 flex items-center justify-center">
              <Languages className="w-4.5 h-4.5 text-white" />
            </div>
            <span className="font-heading text-lg tracking-tight">
              Clinico Translate<sup className="text-[10px] ml-0.5 text-zinc-500">™</sup>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900"
            data-testid="brand-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Press &amp; Brand</p>
          <h1 className="text-4xl sm:text-5xl font-heading tracking-tight text-zinc-950 mb-3">
            Social media kit
          </h1>
          <p className="text-base text-zinc-600 max-w-2xl leading-relaxed">
            Download-ready graphics for Instagram, LinkedIn, X, Facebook, and YouTube. Every asset
            uses the current Fresh Clinical color palette. Tap any download button to save the
            original PNG.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href="/social/clinico-translate-social-kit.zip"
              download="clinico-translate-social-kit.zip"
              data-testid="brand-download-all-zip"
            >
              <Button className="h-11 gap-2" data-testid="brand-download-all-button">
                <Package className="w-4 h-4" />
                Download entire kit (.zip)
              </Button>
            </a>
            <span className="text-xs text-zinc-500">10 PNGs · 2 videos · ~11 MB</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="brand-asset-grid">
          {ASSETS.map((asset) => (
            <div
              key={asset.key}
              className="group bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`brand-asset-card-${asset.key}`}
            >
              <div className="aspect-square bg-zinc-100 flex items-center justify-center overflow-hidden">
                <img
                  src={asset.src}
                  alt={asset.title}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  data-testid={`brand-asset-image-${asset.key}`}
                />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-heading text-base text-zinc-950">{asset.title}</p>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1 whitespace-nowrap">
                    {asset.size}
                  </span>
                </div>
                <p className="text-xs text-zinc-500 mb-4 leading-relaxed">{asset.use}</p>
                <div className="flex items-center gap-2">
                  <a
                    href={asset.src}
                    download={filenameFromSrc(asset.src)}
                    className="flex-1"
                    data-testid={`brand-download-link-${asset.key}`}
                  >
                    <Button
                      variant="default"
                      className="w-full h-9 gap-1.5 text-sm"
                      data-testid={`brand-download-button-${asset.key}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                      Download
                    </Button>
                  </a>
                  <a
                    href={asset.src}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`brand-open-link-${asset.key}`}
                  >
                    <Button
                      variant="outline"
                      className="h-9 text-sm"
                      data-testid={`brand-open-button-${asset.key}`}
                    >
                      Open
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 border-t border-zinc-200 pt-8">
          <div className="flex items-center gap-2 mb-6">
            <Film className="w-5 h-5 text-zinc-700" />
            <h2 className="font-heading text-2xl tracking-tight text-zinc-950">Video ads</h2>
          </div>
          <p className="text-sm text-zinc-600 mb-6 max-w-2xl">
            52-second brand spots in the Fresh Clinical palette. Voiceover, motion typography, and
            ambient soundtrack included — drop straight into your ad campaign.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="brand-video-grid">
            {VIDEOS.map((v) => (
              <div
                key={v.key}
                className="bg-white border border-zinc-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`brand-video-card-${v.key}`}
              >
                <div className={`bg-zinc-950 flex items-center justify-center ${v.key === "ad-vertical" ? "aspect-[9/16] max-h-[560px]" : "aspect-square"}`}>
                  <video
                    src={v.src}
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-contain"
                    data-testid={`brand-video-player-${v.key}`}
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <p className="font-heading text-base text-zinc-950">{v.title}</p>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1 whitespace-nowrap">
                      {v.size}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mb-4 leading-relaxed">{v.use}</p>
                  <div className="flex items-center gap-2">
                    <a
                      href={v.src}
                      download={filenameFromSrc(v.src)}
                      className="flex-1"
                      data-testid={`brand-video-download-link-${v.key}`}
                    >
                      <Button className="w-full h-9 gap-1.5 text-sm" data-testid={`brand-video-download-button-${v.key}`}>
                        <Download className="w-3.5 h-3.5" />
                        Download .mp4
                      </Button>
                    </a>
                    <a
                      href={v.src}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`brand-video-open-link-${v.key}`}
                    >
                      <Button variant="outline" className="h-9 text-sm" data-testid={`brand-video-open-button-${v.key}`}>
                        Open
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 border-t border-zinc-200 pt-8">
          <h2 className="font-heading text-lg text-zinc-950 mb-2">Usage guidelines</h2>
          <ul className="text-sm text-zinc-600 space-y-1.5 list-disc pl-5">
            <li>&quot;Clinico Translate&quot;<sup>™</sup> is a trademark of Wellness and Lifestyle Strategies LLC.</li>
            <li>Do not alter the wordmark, distort the icon, or change the color palette.</li>
            <li>Feel free to crop feed posts for other aspect ratios as needed.</li>
            <li>For custom co-branded assets, email <a href="mailto:BrittneyM@clinicotranslate.com" className="underline underline-offset-2 text-zinc-900">BrittneyM@clinicotranslate.com</a>.</li>
          </ul>
        </div>
      </main>
    </div>
  );
}
